import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useMemo,
  SyntheticEvent,
} from 'react';

import {
  useAutomationsDispatch,
  useAutomationsSelector,
} from 'pages/AutomationEngine/store/react';
import { actions } from 'pages/AutomationEngine/store';
import { format } from 'date-fns';
import {
  HistorySideBar,
  ButtonWrapper,
  HistoryWrapperPage,
  ButtonCard,
  NumberWrapper,
  HistoryButtons,
  HistoryButton,
  TopGradient,
  BottomGradient,
} from './styles';

import { errorByStatus, errorsToShow, linksToShow } from './ts-helpers';

import { ExecutionHistoryContext } from 'ts-components/AutomationEngine/ExecutionHistory/context';
import { grayScale } from 'app/colors';
import { Button as ButtonKDS } from '@kizen/kds/Button';

import type {
  ExecutionHistory,
  Step,
  KizenNodeType,
  HistoryDetailedLog,
} from 'pages/AutomationEngine/types';

type HistoryWithStep = ExecutionHistory & { step: Step | undefined };

import {
  historyStatus,
  historyColors,
  getHistoryColor,
  StyledErrorLink,
  statusDecoration,
} from './Engine/Steps/ConfiguredStepCard/History';
import { useTranslation } from 'react-i18next';
import { KizenTypography } from '__app/typography';
import { getSteps, getTriggers } from './store/selectors';
import { TFunction } from 'i18next';
import { useKeyboardShortcut } from './hooks/useKeyboardShortcut';
import { useVerticalScroll } from './hooks/useVerticalScroll';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';
import {
  getDate,
  formatTimestamp,
} from 'components/Inputs/DateTimeInput/helpers';
import { VARIABLE_DATA_TYPES } from 'ts-components/inputs/ValueInput/types';

const stepTitle = (type: KizenNodeType, t: TFunction) => {
  switch (type) {
    case 'condition':
      return t('Condition');
    case 'goal':
      return t('Goal');
    case 'delay':
      return t('Delay');
    default:
      return t('Action');
  }
};

const historyTitle = (history: HistoryWithStep, t: TFunction) => {
  return history.stepId && history.step
    ? stepTitle(history.step.type, t)
    : history.triggerId
      ? t('Trigger')
      : t('Variable');
};

const useHistoryNavigation = () => {
  const dispatch = useAutomationsDispatch();
  const steps: Step[] = useAutomationsSelector(getSteps);
  const triggers: Step[] = useAutomationsSelector(getTriggers);

  const {
    setSelectedHistoryStepIndex,
    selectedHistoryStepIndex,
    orderedExecutionHistory: _orderedExecutionHistory,
  } = useContext(ExecutionHistoryContext);

  const orderedExecutionHistory: HistoryWithStep[] = useMemo(
    () =>
      _orderedExecutionHistory.map((history) => {
        let step: Step | undefined;
        if (history.stepId) {
          step = steps.find((step: Step) => step.id === history.stepId);
          return {
            ...history,
            step: step,
          };
        }
        return {
          ...history,
          step: triggers.find(
            (trigger: Step) => trigger.id === history.triggerId
          ),
        };
      }),
    [_orderedExecutionHistory, steps, triggers]
  );

  const handleClick = useCallback(
    (index: number) => {
      setSelectedHistoryStepIndex(index);
      const selectedHistoryStep = orderedExecutionHistory[index];
      dispatch(
        actions.setFocusStep({
          step: selectedHistoryStep.stepId || selectedHistoryStep.triggerId,
        })
      );
    },
    [setSelectedHistoryStepIndex, orderedExecutionHistory, dispatch]
  );

  const clickUp = useCallback(() => {
    if (selectedHistoryStepIndex > 0) {
      handleClick(selectedHistoryStepIndex - 1);
    }
  }, [selectedHistoryStepIndex, handleClick]);

  const clickDown = useCallback(() => {
    if (selectedHistoryStepIndex < orderedExecutionHistory.length - 1) {
      handleClick(selectedHistoryStepIndex + 1);
    }
  }, [selectedHistoryStepIndex, orderedExecutionHistory, handleClick]);

  return { handleClick, clickUp, clickDown, orderedExecutionHistory };
};

const getHistoryVariableValue = (detailedLog: HistoryDetailedLog) => {
  const { value, valueHumanReadable, variableSourceSubtype, variableType } =
    detailedLog;

  // these types will always have valueHumanReadable supplied
  // so we can by pass the sub type checking

  if (
    variableType === VARIABLE_DATA_TYPES.employee ||
    variableType === VARIABLE_DATA_TYPES.entity
  ) {
    return valueHumanReadable || value || '';
  }

  switch (variableSourceSubtype) {
    case VARIABLE_DATA_TYPES.date:
      return getDate(value);
    case VARIABLE_DATA_TYPES.datetime:
      return formatTimestamp(value);
    case VARIABLE_DATA_TYPES.employee:
    case VARIABLE_DATA_TYPES.entity:
      return valueHumanReadable;
    default:
      return value || '';
  }
};

export const HistoryVariableValue = ({
  detailedLog,
}: {
  detailedLog: HistoryDetailedLog;
}) => {
  return detailedLog?.variableName ? (
    <TextEllipsisWithTooltip>
      {`${detailedLog.variableName} = "${getHistoryVariableValue(detailedLog)}"`}
    </TextEllipsisWithTooltip>
  ) : null;
};

export const HistoryFrameWork = ({
  children,
  navHeight,
}: {
  children: ReactNode | ReactNode[];
  navHeight: number;
}) => {
  const { t } = useTranslation();
  const translatedStatus = useMemo(() => historyStatus(t), [t]);
  const dispatch = useAutomationsDispatch();
  const { containerProps, topFade, bottomFade } = useVerticalScroll([
    children,
    navHeight,
  ]);

  const { historyId, showHistorySideBar, selectedHistoryStepIndex } =
    useContext(ExecutionHistoryContext);

  const { handleClick, clickUp, clickDown, orderedExecutionHistory } =
    useHistoryNavigation();

  const shortcuts = useMemo(
    () => [
      { key: 'ArrowUp', action: clickUp },
      { key: 'ArrowDown', action: clickDown },
    ],
    [clickUp, clickDown]
  );

  useKeyboardShortcut(shortcuts, Boolean(showHistorySideBar && historyId));

  const refs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (
      selectedHistoryStepIndex >= 0 &&
      refs.current[selectedHistoryStepIndex]
    ) {
      refs.current[selectedHistoryStepIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedHistoryStepIndex]);

  const handleShowError = useCallback(
    (event: SyntheticEvent, history: ExecutionHistory) => {
      event.preventDefault();
      dispatch(actions.showHistoryErrorModal([history]));
    },
    [dispatch]
  );

  if (showHistorySideBar && historyId) {
    return (
      <HistoryWrapperPage navHeight={navHeight}>
        <HistorySideBar>
          <HistoryButtons>
            <HistoryButton
              variant="text"
              onClick={clickUp}
              rightIcon="custom-chevron-up"
              disabled={selectedHistoryStepIndex === 0}
              color="secondary"
            >
              {t('Previous')}
            </HistoryButton>
            <HistoryButton
              variant="text"
              onClick={clickDown}
              rightIcon="custom-chevron-down"
              disabled={
                selectedHistoryStepIndex === orderedExecutionHistory.length - 1
              }
              color="secondary"
            >
              {t('Next')}
            </HistoryButton>
          </HistoryButtons>
          <ButtonWrapper
            className="kds-expanding-scrollbar"
            {...containerProps}
          >
            <TopGradient position="top" fadeIn={topFade} />
            {orderedExecutionHistory.map(
              (history: HistoryWithStep, index: number) => {
                const hasError =
                  errorByStatus(history.status) || errorsToShow(history);
                const hasLink = linksToShow(history);

                const status =
                  translatedStatus[history.status] || history.status;

                const adjustedIndex = index + 1; // Adjust index to start from 1

                const { created, updated, detailedLog } = history;

                const statusColor = hasError
                  ? historyColors.failed
                  : getHistoryColor(history);

                const stepOutput = statusDecoration(history, history!.step, t);

                const hist = historyTitle(history, t);
                return (
                  <ButtonCard
                    key={history.id}
                    onClick={() => handleClick(index)}
                    color={grayScale.white}
                    hoverColor={grayScale.light}
                    disabled={selectedHistoryStepIndex === index}
                    ref={(el) => (refs.current[index] = el)}
                  >
                    <div className="circle">
                      <NumberWrapper
                        color={
                          hasError ? historyColors.failed : grayScale.medium
                        }
                      >
                        {adjustedIndex}
                      </NumberWrapper>
                    </div>
                    <div className="text">
                      <div className="status">
                        <KizenTypography as="div" size="button" weight="bold">
                          {hist}
                        </KizenTypography>
                        {hasError ? (
                          <ButtonKDS
                            variant="text"
                            rightIcon="message-warning-triangle"
                            rightIconSettings={{
                              tooltipDescriptionOverride: t('Failed'),
                            }}
                            color="warning"
                            size="small"
                            onClick={(event) => handleShowError(event, history)}
                          />
                        ) : null}
                      </div>
                      <KizenTypography as="div" size="button">
                        {t('Queued at {{queued}}', {
                          queued: format(created, 'MM/dd/yyyy HH:mm:ss'),
                        })}
                      </KizenTypography>
                      <KizenTypography as="div" size="button">
                        {t(`Run at {{run}} in {{time}} ms`, {
                          run: format(updated, 'MM/dd/yyyy HH:mm:ss'),
                          time:
                            new Intl.NumberFormat().format(
                              history.executionTimeMs
                            ) || 0,
                        })}
                      </KizenTypography>
                      <div>
                        <KizenTypography as="span" size="button">
                          {t('Status: ')}
                        </KizenTypography>
                        {hasError ? (
                          <StyledErrorLink
                            as="span"
                            size="button"
                            weight="bold"
                            type="link"
                            onClick={(event) => handleShowError(event, history)}
                          >
                            {status}
                            {stepOutput}
                          </StyledErrorLink>
                        ) : hasLink ? (
                          <KizenTypography
                            as="span"
                            size="button"
                            weight="bold"
                            type="link"
                            color={statusColor}
                            onClick={(event) => handleShowError(event, history)}
                          >
                            {status}
                            {stepOutput}
                          </KizenTypography>
                        ) : (
                          <KizenTypography
                            as="span"
                            size="button"
                            weight="bold"
                            color={statusColor}
                          >
                            {status}
                            {stepOutput}
                          </KizenTypography>
                        )}
                      </div>
                      {!hasError && detailedLog ? (
                        <HistoryVariableValue detailedLog={detailedLog} />
                      ) : null}
                    </div>
                  </ButtonCard>
                );
              }
            )}
          </ButtonWrapper>
          <BottomGradient position="bottom" fadeIn={bottomFade} />
        </HistorySideBar>
        {children}
      </HistoryWrapperPage>
    );
  }

  return <>{children}</>;
};
